import React, { useState } from 'react';
import { UserIcon, PlusIcon, TrashIcon, PencilIcon, SendIcon, CheckIcon, XIcon, EyeIcon, EyeOffIcon } from 'lucide-react';
// Sample data for public notes
const publicNotes = [{
  id: 1,
  author: {
    name: 'Marco Rossi',
    avatar: 'https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?ixlib=rb-1.2.1&ixid=eyJhcHBfaWQiOjEyMDd9&auto=format&fit=facearea&facepad=2&w=256&h=256&q=80'
  },
  title: 'Economia circolare - Lezione 3',
  content: "I principali modelli di business dell'economia circolare includono: prodotto come servizio, recupero delle risorse, estensione del ciclo di vita, piattaforme di condivisione e forniture circolari.",
  date: '2 giorni fa',
  lesson: 'Lezione 3: Modelli di Business Circolari'
}, {
  id: 2,
  author: {
    name: 'Laura Bianchi',
    avatar: 'https://images.unsplash.com/photo-1494790108377-be9c29b29330?ixlib=rb-1.2.1&ixid=eyJhcHBfaWQiOjEyMDd9&auto=format&fit=facearea&facepad=2&w=256&h=256&q=80'
  },
  title: 'Normative UE sulla circular economy',
  content: "Il Green Deal europeo e il Piano d'azione per l'economia circolare prevedono una serie di iniziative lungo l'intero ciclo di vita dei prodotti, dalla progettazione al riciclo. Importante la direttiva sulla plastic tax e il focus sulla responsabilità estesa del produttore.",
  date: '5 giorni fa',
  lesson: 'Lezione 5: Quadro Normativo e Incentivi'
}, {
  id: 3,
  author: {
    name: 'Alessandro Verdi',
    avatar: 'https://images.unsplash.com/photo-1500648767791-00dcc994a43e?ixlib=rb-1.2.1&ixid=eyJhcHBfaWQiOjEyMDd9&auto=format&fit=facearea&facepad=2&w=256&h=256&q=80'
  },
  title: 'Case study: Patagonia',
  content: "Patagonia rappresenta un esempio eccellente di economia circolare nel settore dell'abbigliamento. Il programma Worn Wear permette di riparare, riutilizzare e riciclare i capi usati, riducendo l'impatto ambientale e promuovendo un consumo più consapevole.",
  date: '1 settimana fa',
  lesson: 'Lezione 4: Case Studies di Successo'
}];
// Sample data for personal notes
const personalNotesData = [{
  id: 1,
  title: 'Appunti lezione introduttiva',
  content: 'Definizione di economia circolare: sistema economico pianificato per riutilizzare i materiali in successivi cicli produttivi, riducendo al massimo gli sprechi.',
  date: '3 settimane fa',
  lesson: "Lezione 1: Introduzione all'Economia Circolare",
  isPublic: false
}, {
  id: 2,
  title: 'Strategie di design circolare',
  content: "Design for disassembly, design for durability, design for remanufacturing. Importante considerare l'intero ciclo di vita del prodotto fin dalla fase di progettazione.",
  date: '2 settimane fa',
  lesson: 'Lezione 2: Design Circolare',
  isPublic: true
}, {
  id: 3,
  title: 'Metriche di circolarità',
  content: "Material Circularity Indicator (MCI), Circular Economy Indicator Prototype (CEIP), Circular Economy Toolkit (CET). Da approfondire il calcolo dell'MCI per la prossima esercitazione.",
  date: '1 settimana fa',
  lesson: 'Lezione 6: Misurazione della Circolarità',
  isPublic: false
}];
const GroupNotes: React.FC<{
  groupId?: string;
}> = ({
  groupId
}) => {
  const [personalNotes, setPersonalNotes] = useState(personalNotesData);
  const [newNote, setNewNote] = useState({
    title: '',
    content: '',
    lesson: ''
  });
  const [isAddingNote, setIsAddingNote] = useState(false);
  const [editingNoteId, setEditingNoteId] = useState<number | null>(null);
  const toggleNoteVisibility = (noteId: number) => {
    setPersonalNotes(personalNotes.map(note => note.id === noteId ? {
      ...note,
      isPublic: !note.isPublic
    } : note));
  };
  const deleteNote = (noteId: number) => {
    setPersonalNotes(personalNotes.filter(note => note.id !== noteId));
  };
  const handleAddNote = () => {
    if (!newNote.title.trim() || !newNote.content.trim()) return;
    const newNoteItem = {
      id: Date.now(),
      title: newNote.title,
      content: newNote.content,
      lesson: newNote.lesson || 'Nota generica',
      date: 'Oggi',
      isPublic: false
    };
    setPersonalNotes([newNoteItem, ...personalNotes]);
    setNewNote({
      title: '',
      content: '',
      lesson: ''
    });
    setIsAddingNote(false);
  };
  const startEditingNote = (note: any) => {
    setEditingNoteId(note.id);
    setNewNote({
      title: note.title,
      content: note.content,
      lesson: note.lesson
    });
  };
  const updateNote = () => {
    if (!newNote.title.trim() || !newNote.content.trim()) return;
    setPersonalNotes(personalNotes.map(note => note.id === editingNoteId ? {
      ...note,
      title: newNote.title,
      content: newNote.content,
      lesson: newNote.lesson || note.lesson
    } : note));
    setNewNote({
      title: '',
      content: '',
      lesson: ''
    });
    setEditingNoteId(null);
  };
  const cancelEdit = () => {
    setNewNote({
      title: '',
      content: '',
      lesson: ''
    });
    setEditingNoteId(null);
    setIsAddingNote(false);
  };
  return <div className="grid grid-cols-1 md:grid-cols-3 gap-6" data-id="element-1229">
      {/* Public notes from group members - takes 2/3 of the space */}
      <div className="md:col-span-2 space-y-4" data-id="element-1230">
        <div className="bg-white rounded-lg border border-gray-200 p-4" data-id="element-1231">
          <h2 className="text-lg font-semibold text-gray-800 mb-4" data-id="element-1232">
            Note condivise dal gruppo
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-id="element-1233">
            {publicNotes.map(note => <div key={note.id} className="border border-gray-200 rounded-lg p-4 hover:border-gray-300 transition-colors" data-id="element-1234">
                <div className="bg-gray-100 text-gray-700 text-sm font-medium px-3 py-1 rounded-md mb-3 inline-block" data-id="element-1235">
                  {note.lesson}
                </div>
                <div className="flex items-center mb-3" data-id="element-1236">
                  <div className="w-8 h-8 rounded-full overflow-hidden mr-2" data-id="element-1237">
                    <img src={note.author.avatar} alt={note.author.name} className="w-full h-full object-cover" data-id="element-1238" />
                  </div>
                  <div data-id="element-1239">
                    <h3 className="font-medium text-gray-800" data-id="element-1240">
                      {note.author.name}
                    </h3>
                  </div>
                </div>
                <div data-id="element-1241">
                  <h3 className="font-medium text-gray-800 mb-1" data-id="element-1242">
                    {note.title}
                  </h3>
                  <p className="text-sm text-gray-600" data-id="element-1243">{note.content}</p>
                </div>
              </div>)}
          </div>
        </div>
      </div>
      {/* User's personal notes - takes 1/3 of the space */}
      <div className="md:col-span-1 space-y-4" data-id="element-1244">
        <div className="bg-white rounded-lg border border-gray-200 p-4" data-id="element-1245">
          <div className="flex justify-between items-center mb-4" data-id="element-1246">
            <h2 className="text-lg font-semibold text-gray-800" data-id="element-1247">Le mie note</h2>
            <button onClick={() => setIsAddingNote(true)} className="bg-custom-green text-white p-2 rounded-full hover:bg-custom-green/90 transition-colors" data-id="element-1248">
              <PlusIcon size={16} data-id="element-1249" />
            </button>
          </div>
          {/* Add/Edit note form */}
          {(isAddingNote || editingNoteId !== null) && <div className="mb-4 border border-gray-200 rounded-lg p-4 bg-gray-50" data-id="element-1250">
              <h3 className="font-medium text-gray-800 mb-2" data-id="element-1251">
                {editingNoteId !== null ? 'Modifica nota' : 'Aggiungi nota'}
              </h3>
              <div className="space-y-3" data-id="element-1252">
                <div data-id="element-1253">
                  <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-1254">
                    Titolo
                  </label>
                  <input type="text" value={newNote.title} onChange={e => setNewNote({
                ...newNote,
                title: e.target.value
              })} className="w-full p-2 border border-gray-200 rounded-md focus:outline-none focus:ring-1 focus:ring-custom-green" placeholder="Titolo della nota" data-id="element-1255" />
                </div>
                <div data-id="element-1256">
                  <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-1257">
                    Contenuto
                  </label>
                  <textarea value={newNote.content} onChange={e => setNewNote({
                ...newNote,
                content: e.target.value
              })} className="w-full p-2 border border-gray-200 rounded-md focus:outline-none focus:ring-1 focus:ring-custom-green min-h-[100px]" placeholder="Contenuto della nota" data-id="element-1258" />
                </div>
                <div data-id="element-1259">
                  <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-1260">
                    Lezione
                  </label>
                  <input type="text" value={newNote.lesson} onChange={e => setNewNote({
                ...newNote,
                lesson: e.target.value
              })} className="w-full p-2 border border-gray-200 rounded-md focus:outline-none focus:ring-1 focus:ring-custom-green" placeholder="Es: Lezione 3: Modelli di Business Circolari" data-id="element-1261" />
                </div>
                <div className="flex justify-end space-x-2 pt-2" data-id="element-1262">
                  <button onClick={cancelEdit} className="px-3 py-1 text-sm text-gray-600 hover:text-gray-800" data-id="element-1263">
                    Annulla
                  </button>
                  <button onClick={editingNoteId !== null ? updateNote : handleAddNote} className="px-3 py-1 bg-custom-green text-white rounded-md hover:bg-custom-green/90 text-sm flex items-center" data-id="element-1264">
                    <SendIcon size={14} className="mr-1" data-id="element-1265" />
                    {editingNoteId !== null ? 'Aggiorna' : 'Salva'}
                  </button>
                </div>
              </div>
            </div>}
          <div className="grid grid-cols-1 gap-3" data-id="element-1266">
            {personalNotes.length === 0 ? <p className="text-center text-gray-500 py-4" data-id="element-1267">
                Non hai ancora creato note per questo corso
              </p> : personalNotes.map(note => <div key={note.id} className="border border-gray-200 rounded-lg p-3 transition-colors" data-id="element-1268">
                  <div className="bg-gray-100 text-gray-700 text-sm font-medium px-3 py-1 rounded-md mb-3 inline-block" data-id="element-1269">
                    {note.lesson}
                  </div>
                  <div className="flex justify-between items-start" data-id="element-1270">
                    <h3 className="font-medium text-gray-800" data-id="element-1271">{note.title}</h3>
                    <div className="flex space-x-1" data-id="element-1272">
                      <button onClick={() => startEditingNote(note)} className="p-1.5 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-full transition-colors" title="Modifica" data-id="element-1273">
                        <PencilIcon size={16} data-id="element-1274" />
                      </button>
                      <button onClick={() => deleteNote(note.id)} className="p-1.5 text-gray-400 hover:text-red-500 hover:bg-red-50 rounded-full transition-colors" title="Elimina" data-id="element-1275">
                        <TrashIcon size={16} data-id="element-1276" />
                      </button>
                    </div>
                  </div>
                  <p className="text-sm text-gray-600 mt-1 mb-2" data-id="element-1277">
                    {note.content}
                  </p>
                  <div className="flex justify-end items-center mt-3 pt-2 border-t border-gray-100" data-id="element-1278">
                    <button onClick={() => toggleNoteVisibility(note.id)} className={`px-3 py-1 rounded-md text-xs flex items-center transition-colors ${note.isPublic ? 'bg-custom-green text-white hover:bg-custom-green/90' : 'bg-white text-gray-700 border border-gray-200 hover:bg-gray-50'}`} data-id="element-1279">
                      {note.isPublic ? <>
                          <EyeIcon size={12} className="mr-1" data-id="element-1280" />
                          Pubblica
                        </> : <>
                          <EyeOffIcon size={12} className="mr-1" data-id="element-1281" />
                          Privata
                        </>}
                    </button>
                  </div>
                </div>)}
          </div>
        </div>
      </div>
    </div>;
};
export default GroupNotes;